// Entra auth on WEB (react-native-web / webpack): OAuth2 Authorization Code + PKCE in the
// browser, same as the cc-attack SPA client.
// Webpack resolves this through the .web.ts extension (the native .ts module is not loaded on web).

import { AUTHORITY, SCOPES, decodeJwt, getEntraConfig, type EntraClaims } from './entraConfig';
import { resolveApiBase } from '../config/env';
import { traceAuth } from './authTrace';

export interface Session {
  accessToken: string;
  expiresAt: number; // epoch seconds
  claims: EntraClaims;
  refreshToken?: string; // si offline_access -> permet le refresh silencieux
}

const PKCE_KEY = 'ccjaw.pkce';
const SESSION_KEY = 'ccjaw.session';
const RETURN_TO_KEY = 'ccjaw.returnTo';

/** Remembers the target page (relative URL) to restore after authentication. */
export function rememberReturnTo(path: string): void {
  if (typeof window === 'undefined' || !path) return;
  // Never remember the auth pages themselves.
  if (path.startsWith('/auth/callback') || path.startsWith('/logout/callback')) return;
  sessionStorage.setItem(RETURN_TO_KEY, path);
}
/** Reads and clears the target page. */
export function consumeReturnTo(): string | null {
  if (typeof window === 'undefined') return null;
  const v = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return v;
}

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function randomString(len: number): string {
  const a = new Uint8Array(len);
  crypto.getRandomValues(a);
  return b64url(a);
}
async function sha256b64url(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return b64url(new Uint8Array(digest));
}

export async function startLogin(signup = false, returnTo?: string): Promise<void> {
  const cfg = getEntraConfig();
  // An explicit returnTo is stored; otherwise keep the one already remembered (guard).
  if (returnTo) rememberReturnTo(returnTo);
  const verifier = randomString(48);
  const state = randomString(16);
  const challenge = await sha256b64url(verifier);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));

  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: 'code',
    redirect_uri: cfg.redirectUri,
    response_mode: 'query',
    scope: SCOPES.join(' '),
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  if (signup) params.set('prompt', 'create');
  window.location.assign(`${AUTHORITY}/oauth2/v2.0/authorize?${params.toString()}`);
}

export async function completeLoginIfCallback(): Promise<Session | null> {
  if (typeof window === 'undefined' || window.location.pathname !== '/auth/callback') {
    // Clean up the logout round-trip.
    if (typeof window !== 'undefined' && window.location.pathname === '/logout/callback') {
      localStorage.removeItem(SESSION_KEY); sessionStorage.removeItem(SESSION_KEY);
      window.history.replaceState({}, document.title, '/');
    }
    return null;
  }
  const qs = new URLSearchParams(window.location.search);
  const code = qs.get('code');
  const err = qs.get('error');
  const cleanup = () => window.history.replaceState({}, document.title, '/');
  if (err) {
    cleanup();
    throw new Error(`${err}: ${qs.get('error_description') || ''}`);
  }
  if (!code) {
    cleanup();
    return null;
  }
  const saved = JSON.parse(sessionStorage.getItem(PKCE_KEY) || '{}');
  if (!saved.verifier || saved.state !== qs.get('state')) {
    cleanup();
    throw new Error('invalid PKCE state');
  }
  const cfg = getEntraConfig();
  const body = new URLSearchParams({
    client_id: cfg.clientId,
    grant_type: 'authorization_code',
    code,
    redirect_uri: cfg.redirectUri,
    code_verifier: saved.verifier,
    scope: SCOPES.join(' '),
  });
  let res: Response;
  try {
    res = await fetch(`${AUTHORITY}/oauth2/v2.0/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (e: any) {
    cleanup();
    throw new Error(
      'Code exchange blocked (CORS) — add an SPA platform with ' +
        cfg.redirectUri +
        ' on cc-attack-jaw-spa-react. ' +
        (e?.message || ''),
    );
  }
  const data = await res.json();
  if (!res.ok) {
    cleanup();
    throw new Error(`token: ${data.error} ${data.error_description || ''}`);
  }
  const session: Session = {
    accessToken: data.access_token,
    expiresAt: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
    claims: decodeJwt(data.access_token),
    refreshToken: data.refresh_token,
  };
  // ⚠️ IN MEMORY, NOT IN STORAGE — CCT-320. Same rule as the OTP flow: nothing durable
  // in the browser. The proxy has already parked the coupon in the HttpOnly cookie.
  storeSession(session);
  sessionStorage.removeItem(PKCE_KEY);
  cleanup();
  return session;
}

/** Exchanges a refresh_token for a new access token (grant_type=refresh_token). */
async function refreshSession(refreshToken: string): Promise<Session | null> {
  const cfg = getEntraConfig();
  // ⚠️ NO `refresh_token` HERE — CCT-320. The coupon lives in an HttpOnly cookie that the
  // browser attaches to `/auth/native/*` on its own, and the proxy injects it server-side.
  // Sending one from JavaScript would mean JavaScript still holds one, which is the exact
  // thing this change removes. The parameter is kept in the signature only so the native
  // implementation (`entraAuth.ts`, which has no cookies) can go on passing it.
  const body = new URLSearchParams({
    client_id: cfg.clientId,
    grant_type: 'refresh_token',
    scope: SCOPES.join(' '),
  });
  try {
    // ⚠️ THROUGH THE BACKEND PROXY, NEVER DIRECT (CCT-315). This call used to target
    // `ciamlogin.com` straight from the browser, and Entra ALWAYS refused it:
    //
    //   AADSTS9002326: Cross-origin token redemption is permitted only for the
    //   'Single-Page Application' client-type. Request origin: 'http://localhost:3000'
    //
    // So the refresh had NEVER worked — not once, for anyone — and the app logged everyone out
    // once the 94-minute access token expired. Login kept working because it already goes
    // through this same proxy (`/auth/native`), whose whole reason to exist is that the native
    // endpoints emit no CORS headers. Only the refresh had been left behind.
    //
    // ⚠️ AND IT IS *NOT* A MISDECLARED PLATFORM — that is the tempting wrong reading of the
    // message, and it cost time. MEASURED on the CIAM tenant: the registration declares 5 SPA
    // redirect URIs, an empty Web platform, an empty public-client platform, and
    // `isFallbackPublicClient: true`. Adding the bare origin changed nothing (4 attempts, then
    // reverted). The registration is a PUBLIC NATIVE client, as Native Authentication requires,
    // so the refresh token is issued by a native flow and Entra will not let a browser redeem
    // it whatever SPA URIs are also declared. No portal setting fixes this; the proxy is the
    // only route.
    //
    // Server-to-server: no browser origin, hence no client-type restriction.
    //
    // REPRODUCED as a controlled experiment (2026-08-13): same refresh token, same client_id,
    // same scope, same instant, only the destination differing -> direct = 400 AADSTS9002326,
    // proxy = 200 with a token. Reverting this line logs the user out on the next reload.
    const res = await fetch(`${resolveApiBase()}/auth/native/oauth2/v2.0/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      // ⚠️ WITHOUT THIS THE COOKIE NEITHER TRAVELS NOR GETS STORED. The API is a different
      // ORIGIN (another port locally, another sub-domain in the cloud), and a cross-origin
      // fetch omits cookies unless asked. It fails silently: the request goes out, the server
      // answers, and the renewal simply never has a coupon.
      credentials: 'include',
    });
    if (!res.ok) {
      // ⚠️ SAY WHY. A bare `return null` is what made this defect RECURRENT: telemetry could
      // only ever record "refresh: failed", while the response body carried the exact code.
      // Every occurrence therefore started the investigation from scratch.
      let raison = `http_${res.status}`;
      try {
        const err: any = await res.json();
        const code = String(err?.error_description || '').match(/AADSTS\d+/)?.[0];
        raison = [err?.error, code].filter(Boolean).join(' ') || raison;
      } catch { /* unreadable body: keep the status */ }
      // ⚠️ THE REASON GOES IN `detail`, NOT IN THE TOKEN SLOT. It used to be passed as the
      // second argument, which is where `traceAuth` expects a TOKEN: the string was therefore
      // HASHED into a fingerprint and the cause vanished. Measured on screen: `refresh-ko
      // 5aa6e4e4 exp dans -32071s`, a fingerprint identical to the token's and not one word of
      // cause. The file already demanded "say why"; the call erased the answer.
      dernierVerdict = 'refuse';       // the server answered, and it said no
      traceAuth('refresh-ko', null, raison);
      return null;
    }
    const data = await res.json();
    if (!data.access_token) {
      // ⚠️ A 200 WITH NO TOKEN IS NOT A SUCCESS, and it left here WITHOUT A WORD. It happens
      // when the proxy answers an unexpected payload; indistinguishable from a cut network in the
      // journal, hence never diagnosed.
      dernierVerdict = 'refuse';
      traceAuth('refresh-ko', null, 'reponse_200_sans_access_token');
      return null;
    }
    const session: Session = {
      accessToken: data.access_token,
      expiresAt: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
      claims: decodeJwt(data.access_token),
      refreshToken: data.refresh_token || refreshToken, // Entra may return a rotating RT
    };
    dernierVerdict = null;             // succeeded: the previous verdict is worthless now
    storeSession(session);
    return session;
  } catch (exc) {
    // ⚠️ THIS BARE `catch` IS WHAT MADE THE FAILURE UNDIAGNOSABLE. The comment next door says
    // it in plain words — "SAY WHY. A bare `return null` is what made this defect RECURRENT" —
    // and the exception exit had never been instrumented. That is the way the 2026-08-18
    // sign-out went: `refresh-ko` with no cause, and NOTHING server-side, because a request that
    // never completes leaves no trace there.
    dernierVerdict = 'injoignable';    // no answer: we conclude NOTHING about the coupon
    traceAuth('refresh-ko', null, `exception ${(exc as any)?.name || typeof exc}`);
    return null;
  }
}

/** Refreshes a Google session (in-house HS256) through the backend. */
async function refreshGoogleSession(refreshToken: string): Promise<Session | null> {
  try {
    const res = await fetch(`${resolveApiBase()}/auth/session/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // ⚠️ AN EMPTY BODY MEANS "USE THE COOKIE" — CCT-320. The coupon is no longer held by the
      // browser; the backend reads it from its own HttpOnly cookie. Sending one from here would
      // mean JavaScript still holds one, which is precisely what was removed.
      body: JSON.stringify({}),
      // Without this the cookie neither travels nor gets stored: the API is a different ORIGIN.
      credentials: 'include',
    });
    if (!res.ok) {
      dernierVerdict = 'refuse';
      traceAuth('refresh-ko', null, `google http_${res.status}`);
      return null;
    }
    const data = await res.json();
    if (!data.access_token) {
      dernierVerdict = 'refuse';
      traceAuth('refresh-ko', null, 'google reponse_200_sans_access_token');
      return null;
    }
    const session: Session & { kind?: string } = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token || refreshToken,
      expiresAt: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
      claims: decodeJwt(data.access_token),
      kind: 'google',
    };
    dernierVerdict = null;
    storeSession(session as Session);
    return session;
  } catch (exc) {
    dernierVerdict = 'injoignable';
    traceAuth('refresh-ko', null, `google exception ${(exc as any)?.name || typeof exc}`);
    return null;
  }
}

export async function restoreSession(): Promise<Session | null> {
  if (typeof window === 'undefined') return null;
  // ⚠️ THE COOKIE IS THE SOURCE OF TRUTH AT START-UP — CCT-320. Nothing is persisted any more,
  // so there is no stored session to read: we ask the proxy, which reads the HttpOnly cookie.
  // A failure here is NOT an error — it is simply someone who is not signed in.
  if (sessionEnMemoire) return sessionEnMemoire;

  // ⚠️ ANY LEFTOVER PERSISTED SESSION IS DESTROYED, NEVER READ. A previous version of this app
  // wrote the whole session — access token AND coupon — into `localStorage`. Reading it here
  // "just to migrate gently" would keep serving, and keep trusting, exactly the material this
  // change exists to remove; and it would do so for every user who had signed in before,
  // i.e. all of them. It is deleted on sight instead.
  try {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  } catch { /* private mode */ }

  // ⚠️ TWO COUPONS, TWO COOKIES, AND BOTH MUST BE TRIED. CIAM sign-ins renew through the native
  // proxy; Google sign-ins renew through `/auth/session/refresh`. Trying only the first would
  // sign every Google user out on every reload — a population that exists, since Google is
  // federated for the player app. Neither call can tell in advance which cookie the browser
  // holds, so both are attempted; a failure simply means "not signed in", not an error.
  const parLeCookie = await refreshSession('');
  if (parLeCookie) return parLeCookie;
  const parGoogle = await refreshGoogleSession('');
  if (parGoogle) return parGoogle;
  return null;
}

/**
 * Refreshes the session BEFORE it expires, while the app is open. `null` = "nothing to do".
 *
 * `restoreSession` only runs at start-up: without this companion, the access token (~1 h)
 * expired mid-use, the next API call took a 401, and the interceptor signed the user out
 * ("the session expires quickly"). Here we renew silently as soon as fewer than
 * `marginSeconds` remain.
 *
 * On failure the session is NOT purged: the 401 stays the arbiter, so a passing network
 * hiccup never signs anyone out.
 *
 * ⚠️ Do NOT use this to react to a 401: `null` here means "the token is still valid", which
 * is precisely what is false when the server has just rejected it. Use `forceRefresh()`.
 */
export async function ensureFreshSession(marginSeconds = 300): Promise<Session | null> {
  if (typeof window === 'undefined') return null;
  const s = readSession();
  if (!s) return null;
  const now = Math.floor(Date.now() / 1000);
  if (!s.expiresAt || s.expiresAt - marginSeconds > now) return null;   // still plenty of time
  return await forceRefresh();
}

/**
 * Renews NOW, without looking at `expiresAt`. `null` = the renewal FAILED (refresh_token
 * missing, revoked, or refused) — the only case that warrants signing out.
 *
 * This is the answer to a 401: the client has no business trusting its own clock over the
 * server. A few seconds of drift, a token revoked on Entra's side, a machine running fast…
 * and `ensureFreshSession(0)` answered "still valid" -> `null` -> the interceptor signed the
 * user out WITHOUT ever attempting a renewal. That was the cause of sessions dropping
 * mid-use.
 */
// THE LAST RENEWAL'S VERDICT — CCT-345.
//
// ⚠️ "FAILED" COVERED TWO OPPOSITE SITUATIONS, AND BOTH WERE TREATED ALIKE. A server that
// REFUSES the coupon (`invalid_grant`, `AADSTS…`) is saying the session is dead: retrying will
// never resurrect it. An UNREACHABLE server (network down, backend restarting, machine asleep)
// says nothing at all: retrying is exactly the right move.
//
// Both returned `null`, so the background loop retried for ever in either case. MEASURED on
// 2026-08-18: token expired 8 h 55 earlier with a tick every 60 s, that is roughly 540 renewals
// failing in silence, then a brutal sign-out on the first call the user triggered themselves.
// The session had been dead since the middle of the night and the app kept up appearances.
export type VerdictRenouvellement = 'refuse' | 'injoignable' | null;
let dernierVerdict: VerdictRenouvellement = null;

/** Why the last renewal failed: `refuse` = definitive, `injoignable` = transient, `null` = no
 *  failure. */
export function lastRefreshVerdict(): VerdictRenouvellement { return dernierVerdict; }

/** Is the current token ALREADY expired? No margin: the question is "is it dead", not "should
 *  it be renewed". No session = not expired, there is nothing to conclude. */
export function currentSessionExpired(): boolean {
  const s = readSession();
  if (!s?.expiresAt) return false;
  return s.expiresAt <= Math.floor(Date.now() / 1000);
}

// ONE renewal IN FLIGHT AT A TIME (CCT-127). Entra ROTATES the refresh_token: the first call
// consumes the RT and invalidates the old one. A single screen easily fires 4-5 parallel
// requests: they all took a 401, they all called `forceRefresh`, and only the first
// succeeded — the others got `invalid_grant`, hence `null`, hence "signed out". That is what
// made the drops look RANDOM (they depended on how many calls happened to be concurrent).
// All concurrent callers now share THE SAME promise.
let inflightRefresh: Promise<Session | null> | null = null;

export async function forceRefresh(): Promise<Session | null> {
  if (typeof window === 'undefined') return null;
  if (inflightRefresh) return await inflightRefresh;
  const s = readSession();
  // ⚠️ WE NO LONGER GIVE UP FOR WANT OF A COUPON IN MEMORY — that was the cause of the
  // recurring sign-outs. Since CCT-320 the coupon lives in an HttpOnly cookie the BROWSER
  // attaches, and the proxy injects it server-side ("the cookie always wins"): the client is NOT
  // supposed to hold one. Requiring it here meant giving up before even calling — measured on
  // screen: `exp dans -23466s`, `refresh-ko`, sign-out, and NO server-side trace at all, because
  // the request was never sent.
  //
  // ⚠️ A SESSION IS STILL REQUIRED. Without one there is nothing to renew, and calling would
  // return a 401 we would read as a revocation.
  if (!s) {
    // Traced, because "nothing to renew" and "the renewal was refused" lead to the same screen
    // and call for opposite repairs.
    traceAuth('refresh-ko', null, 'aucune session en memoire');
    return null;
  }
  inflightRefresh = (s.kind === 'google'
    ? refreshGoogleSession(s.refreshToken ?? '')
    : refreshSession(s.refreshToken ?? '')
  ).finally(() => { inflightRefresh = null; });
  return await inflightRefresh;
}

/** No-op on web: the session is read straight from storage (see `currentAccessToken`).
 *  Kept so that callers are identical on both platforms. */
export function rememberAccessToken(_t: string | null): void { /* web: nothing to remember */ }

/** The current session in memory/storage — used to tell whether ANOTHER call has just
 *  renewed (the token changed) before concluding the user is signed out. */
export function currentAccessToken(): string | null {
  return readSession()?.accessToken ?? null;
}

// ⚠️ THE SESSION LIVES IN MEMORY, NOT IN `localStorage` — CCT-320.
//
// It used to be written whole — access token AND refresh token — into storage any script on
// the page can read. MEASURED: a 1504-character coupon, renewable for days, usable from
// anywhere with a `curl`, and out of reach of any client-side sign-out. An XSS took the lot.
//
// The coupon now lives in an HttpOnly cookie. The access token stays here, in a module
// variable: readable by an XSS while the page is open — that cannot be avoided, since the code
// must attach it to every request — but it dies with the tab and expires in ~90 minutes.
// What disappears is the DURABLE, PORTABLE credential.
//
// Consequence, and it is deliberate: a reload no longer finds a session in storage. It asks
// the proxy for a new access token, and the cookie answers. One round-trip at start-up buys a
// browser that keeps nothing worth stealing.
let sessionEnMemoire: (Session & { kind?: string }) | null = null;

/** Adopts a session established elsewhere (the OTP flow) WITHOUT persisting it.
 *  Exported so `nativeAuth.web.ts` has somewhere to hand its session: there must be exactly ONE
 *  place that decides where a session lives, otherwise one of them will keep writing to
 *  `localStorage` and the cookie becomes decoration. */
export function adoptSession(session: Session): void {
  storeSession(session);
}

function storeSession(session: Session): void {
  sessionEnMemoire = session;
  // ⚠️ THE OLD COPY IS ERASED. Leaving it would keep serving the very tokens we just stopped
  // writing: a stale `localStorage` entry from a previous version would go on being read at
  // start-up, and the change would apply to no one who had already signed in.
  try {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  } catch { /* private mode: nothing to clean */ }
}

function readSession(): (Session & { kind?: string }) | null {
  return sessionEnMemoire;
}

export async function logout(): Promise<void> {
  const cfg = getEntraConfig();
  localStorage.removeItem(SESSION_KEY); sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(PKCE_KEY);
  const params = new URLSearchParams({ post_logout_redirect_uri: cfg.postLogoutUri });
  window.location.assign(`${AUTHORITY}/oauth2/v2.0/logout?${params.toString()}`);
}
